import { useLibraryFileUploadAction } from "@/action/use-library-file-upload-action";
import { useAssistContext } from "@/components/Assist/AssistContext";
import { useChatFileLibraryFileData } from "@/data/use-chat-library-files-data";
import {
  selectGroupModelProfile,
  useGroupModelProfilesData,
} from "@/data/use-group-model-profiles-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { SelectedExternalFile } from "@/lib/external-file-browser";
import { externalFileControllerAddExternalFile } from "@/lib/gen-api";
import { queryKeys } from "@/lib/query-keys";
import { AllowedMimeType, File as FileDTO } from "@/types/types";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

export const MAX_FILES_COUNT = 5;

type LibraryFile = {
  uuid: string;
  name: string;
  mimeType: AllowedMimeType;
};

type ChatFileManagerContextType = {
  // Data
  chatUuid: string | null;
  /**
   * Local files selected by the user, still not uploaded.
   */
  selectedLocalFiles: File[];
  /**
   * External files selected by the user, still not uploaded.
   */
  selectedExternalFiles: SelectedExternalFile[];
  /**
   * Library files that have been selected by the user, still not attached to a chat.
   */
  selectedLibraryFiles: LibraryFile[];
  /**
   * Library files that have been attached to a chat.
   */
  chatFiles: FileDTO[];
  /**
   * Whether the file limit has been exceeded.
   */
  isLoading: boolean;
  fileLimitExceeded: boolean;
  allowedMimeTypes: string[];
  filesCount: number;
  // Setters
  clearFileManager: () => void;
  // Handlers
  addLibraryFile: (file: LibraryFile) => Promise<void>;
  removeLibraryFile: (fileUuid: string) => void;
  removeLocalFile: (file: File) => void;
  removeExternalFile: (externalId: string) => void;
  /**
   * If groupUuid and kitUuid are not provided - adds to the selectedLocalFiles.
   * If groupUuid and kitUuid are provided - adds to the selectedLibraryFiles
   * If chatUuid is provided - adds to the chatFiles
   */
  handleLocalFile: () => void;
  /**
   * If groupUuid and kitUuid are not provided - adds to the selectedExternalFiles.
   * If groupUuid and kitUuid are provided - adds to the selectedLibraryFiles
   * If chatUuid is provided - adds to the chatFiles
   */
  handleExternalFile: (file: SelectedExternalFile) => void;
};

const ChatFileManagerContext = createContext<
  ChatFileManagerContextType | undefined
>(undefined);

export const useChatFileManager = () => {
  const context = useContext(ChatFileManagerContext);

  if (!context) {
    throw new Error(
      "useChatFileManager must be used within a ChatFileManagerProvider",
    );
  }

  return context;
};

export const useOptionalChatFileManager = () => {
  return useContext(ChatFileManagerContext);
};

/**
 * Handles the library files pipeline for kit and chat.
 */
export const ChatFileManagerProvider = (props: {
  groupUuid: string | undefined;
  kitUuid: string | undefined | null;
  chatUuid: string | null;
  children: React.ReactNode;
}) => {
  // ============================================================
  // UUIDs
  // ============================================================

  const rootGroupUuid = useGroupUuid();
  const rootKitUuid = useKitUuid();

  const groupUuid =
    props.groupUuid === undefined ? rootGroupUuid : props.groupUuid;
  const kitUuid = props.kitUuid === undefined ? rootKitUuid : props.kitUuid;
  const chatUuid = props.chatUuid ?? props.chatUuid;

  // ============================================================
  // External Hooks
  // ============================================================

  const { chatFiles, isLoading } = useChatFileLibraryFileData({
    chatUuid,
  });

  const { attach, detach } = useChatFileLibraryFileData({
    chatUuid,
  });

  const { modelProfileUuid, activeChatUuid } = useAssistContext();
  const { modelProfiles } = useGroupModelProfilesData(groupUuid);
  const modelProfile = selectGroupModelProfile(modelProfiles, modelProfileUuid);
  const allowedMimeTypes =
    modelProfile?.textModelSettings.allowedMimeTypes ?? [];

  const queryClient = useQueryClient();

  const { browse: browseAction, browseAndUpload: browseAndUploadAction } =
    useLibraryFileUploadAction(groupUuid, kitUuid);

  // ============================================================
  // States
  // ============================================================

  const [selectedLocalFiles, setSelectedLocalFiles] = useState<File[]>([]);
  const [selectedExternalFiles, setSelectedExternalFiles] = useState<
    SelectedExternalFile[]
  >([]);
  const [selectedLibraryFiles, setSelectedLibraryFiles] = useState<
    LibraryFile[]
  >([]);

  const [fileLimitExceeded, setFileLimitExceeded] = useState(false);

  // ============================================================
  // Effects
  // ============================================================

  useEffect(() => {
    if (activeChatUuid) {
      setSelectedLibraryFiles([]);
      setSelectedLocalFiles([]);
      setSelectedExternalFiles([]);
    }
  }, [activeChatUuid]);

  // ============================================================
  // Selectors
  // ============================================================

  const isExistingChat = !!chatUuid;
  const filesCount =
    chatFiles.length +
    selectedLibraryFiles.length +
    selectedLocalFiles.length +
    selectedExternalFiles.length;

  // ============================================================
  // Handlers
  // ============================================================

  const addLibraryFile = async (file: LibraryFile) => {
    if (filesCount >= MAX_FILES_COUNT) {
      setFileLimitExceeded(true);
      return;
    }

    if (isExistingChat) {
      setFileLimitExceeded(false);
      try {
        await attach(file.uuid);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "";
        if (
          msg.includes("FILE_LIMIT_EXCEEDED") ||
          (typeof err === "object" &&
            err !== null &&
            "status" in err &&
            err.status === 400)
        ) {
          setFileLimitExceeded(true);
        } else {
          toast.error("Failed to attach file");
        }
      }
    } else {
      if (selectedLibraryFiles.some((f) => f.uuid === file.uuid)) {
        return;
      }
      setSelectedLibraryFiles((prev) => [...prev, file]);
    }
  };

  const removeLibraryFile = (fileUuid: string) => {
    if (isExistingChat) {
      detach(fileUuid);
    }
    setSelectedLibraryFiles((prev) => prev.filter((x) => x.uuid !== fileUuid));
  };

  const removeLocalFile = (file: File) => {
    setSelectedLocalFiles((prev) => prev.filter((f) => f !== file));
  };

  const removeExternalFile = (externalId: string) => {
    setSelectedExternalFiles((prev) =>
      prev.filter((f) => f.externalId !== externalId),
    );
  };

  const _browseAndSelect = () => {
    browseAction({
      allowedMimeTypes,
      onSelect: (file) => {
        setSelectedLocalFiles((prev) => [...prev, file]);
      },
    });
  };

  const _browseAndUpload = async () => {
    browseAndUploadAction({
      allowedMimeTypes,
      onSelect: (file) => {
        // Queue the file for upload
        setSelectedLocalFiles((prev) => [...prev, file]);
      },
      onUpload: async ({ file, uuid }) => {
        // Remove the file from the queue
        setSelectedLocalFiles((prev) => prev.filter((f) => f !== file));
        await addLibraryFile({
          uuid,
          name: file.name,
          mimeType: file.type as AllowedMimeType,
        });
      },
    });
  };

  const handleLocalFile = () => {
    if (groupUuid && kitUuid) {
      _browseAndUpload();
    } else {
      _browseAndSelect();
    }
  };

  /**
   * Adds external file to upload queue.
   */
  const _selectExternalFile = (file: SelectedExternalFile) => {
    if (selectedExternalFiles.some((f) => f.externalId === file.externalId)) {
      return;
    }
    setSelectedExternalFiles((prev) => [...prev, file]);
  };

  /**
   * Adds external file to upload queue and uploads it.
   */
  const handleExternalFile = async (file: SelectedExternalFile) => {
    _selectExternalFile(file);

    if (!groupUuid || !kitUuid) {
      throw new Error("Group, kit are required");
    }

    const response = await externalFileControllerAddExternalFile({
      body: {
        connectionUuid: file.connectionUuid,
        externalId: file.externalId,
        driveId: file.driveId,
        name: file.name,
        isKnowledge: false,
        isLibrary: true,
        groupUuid: groupUuid,
        kitUuid: kitUuid,
      },
    });

    // Remove file from the queue
    setSelectedExternalFiles((prev) =>
      prev.filter((f) => f.externalId !== file.externalId),
    );

    const fileUuid = response.data?.uuid;

    if (!fileUuid) {
      toast.error("Failed to add external file");
      return;
    }

    setSelectedLibraryFiles((prev) => [
      ...prev,
      {
        uuid: fileUuid,
        name: file.name,
        mimeType: file.mimeType as AllowedMimeType,
      },
    ]);

    if (isExistingChat) {
      attach(fileUuid);

      queryClient.invalidateQueries({
        queryKey: queryKeys.files(),
      });
    }
  };

  const clearFileManager = () => {
    setSelectedLibraryFiles([]);
    setSelectedLocalFiles([]);
    setSelectedExternalFiles([]);
  };

  // ============================================================
  // Rendered
  // ============================================================

  return (
    <ChatFileManagerContext.Provider
      value={{
        // Data
        chatUuid,
        selectedLocalFiles,
        selectedExternalFiles,
        selectedLibraryFiles,
        chatFiles,
        isLoading,
        fileLimitExceeded,
        allowedMimeTypes,
        filesCount,
        // Setters
        clearFileManager,
        // Handlers
        addLibraryFile,
        removeLibraryFile,
        removeLocalFile,
        removeExternalFile,
        handleLocalFile,
        handleExternalFile,
      }}
    >
      {props.children}
    </ChatFileManagerContext.Provider>
  );
};
